import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import AuthModal from "@/components/AuthModal";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, Users } from "lucide-react";

export default function JoinTeam() {
  const { token } = useParams();
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [authOpen, setAuthOpen] = useState(false);
  const [status, setStatus] = useState("idle");
  const triedRef = useRef(false);

  const join = async () => {
    setStatus("joining");
    try {
      const { data } = await api.post("/team/join", { token });
      await refreshUser();
      toast.success(data.message);
      setStatus("done");
      setTimeout(() => navigate("/"), 1500);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not join team");
      setStatus("error");
    }
  };

  useEffect(() => {
    if (user === false) {
      setAuthOpen(true);
    } else if (user && !triedRef.current) {
      triedRef.current = true;
      join();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return (
    <div className="min-h-screen bg-[#0b0c10] text-slate-100 flex items-center justify-center p-6">
      <div className="max-w-md w-full rounded-2xl border border-white/10 bg-[#12161f] p-10 text-center" data-testid="join-team-page">
        <Users className="w-12 h-12 mx-auto amber-text mb-4" />
        <h1 className="font-heading text-3xl uppercase tracking-tight">Join the team</h1>
        {!user && (
          <>
            <p className="text-slate-400 text-sm mt-2">Log in or create a free account to accept this invite.</p>
            <Button onClick={() => setAuthOpen(true)} className="mt-6 w-full bg-[#00f0ff] text-black hover:bg-[#33f4ff] font-bold" data-testid="join-login-button">
              Log in to join
            </Button>
          </>
        )}
        {status === "joining" && <p className="text-slate-400 text-sm mt-4"><Loader2 className="w-4 h-4 inline animate-spin mr-2" />Joining…</p>}
        {status === "done" && <p className="cyan-text text-sm mt-4 font-semibold">You're in! Redirecting…</p>}
        {status === "error" && (
          <>
            <p className="text-[#FF3366] text-sm mt-4" data-testid="join-error-text">
              This invite link is invalid, expired, or the team is full.
            </p>
            <Button onClick={() => navigate("/")} variant="outline" className="mt-4 w-full border-white/15 bg-white/5">Back to studio</Button>
          </>
        )}
      </div>
      <AuthModal open={authOpen} onOpenChange={setAuthOpen} />
    </div>
  );
}
